import { useEffect, useState } from "react";
import { Mail, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const relativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function MessagesPanel() {
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(null); // message id of expanded row
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/messages").then((r) => setMessages(r.data));

  useEffect(() => {
    load();
  }, []);

  const markRead = async (m) => {
    if (m.read) return;
    try {
      await api.put(`/admin/messages/${m.id}/read`);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    } catch (err) {
      // Marking-as-read is a soft UX nicety — a transient failure shouldn't
      // interrupt the editor's workflow. Log for debugging but stay silent.
      console.warn("Failed to mark message as read", err);
    }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete message from "${m.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Deleted");
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="space-y-4" data-testid="admin-messages-panel">
      <div className="brut bg-white p-4 flex items-center gap-3">
        <Mail className="w-5 h-5" strokeWidth={2.5} />
        <div className="font-display text-2xl tracking-tight">Inbox</div>
        <span className="font-mono uppercase text-[10px] tracking-widest opacity-60 ml-auto">
          {messages.length} total · {unreadCount} unread
        </span>
      </div>

      {messages.length === 0 ? (
        <div className="brut bg-white p-6 text-sm text-gray-700" data-testid="messages-empty">
          No messages yet. Your contact form is live at{" "}
          <a href="/page/contact" className="underline hover:pcs-text-pink">
            /page/contact
          </a>
          .
        </div>
      ) : (
        <ul className="brut bg-white divide-y-2 divide-black/10">
          {messages.map((m) => {
            const isOpen = open === m.id;
            return (
              <li
                key={m.id}
                className={`p-4 ${!m.read ? "pcs-bg-cream" : ""}`}
                data-testid={`message-row-${m.id}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpen(isOpen ? null : m.id);
                    if (!m.read) markRead(m);
                  }}
                  data-testid={`message-toggle-${m.id}`}
                  className="w-full text-left grid grid-cols-[auto_1fr_auto] items-center gap-3"
                >
                  {!m.read ? (
                    <span
                      className="w-2 h-2 rounded-full pcs-bg-pink"
                      aria-label="Unread"
                    />
                  ) : (
                    <CheckCircle2 className="w-3 h-3 opacity-40" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-bold">{m.name}</span>
                      <a
                        href={`mailto:${m.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-xs opacity-70 hover:pcs-text-pink"
                      >
                        {m.email}
                      </a>
                      <span className="font-mono text-[10px] uppercase tracking-widest opacity-50 ml-auto">
                        {relativeTime(m.created_at)}
                      </span>
                    </div>
                    {m.subject && (
                      <div className="text-sm opacity-80 mt-0.5">{m.subject}</div>
                    )}
                    {!isOpen && (
                      <div className="text-xs opacity-60 truncate mt-0.5">
                        {m.message.replace(/\s+/g, " ").slice(0, 140)}
                      </div>
                    )}
                  </div>
                </button>

                {isOpen && (
                  <div className="mt-3 space-y-3" data-testid={`message-detail-${m.id}`}>
                    <div className="brut pcs-bg-cream p-3 text-sm whitespace-pre-wrap">
                      {m.message}
                    </div>
                    <div className="flex justify-end gap-2">
                      <a
                        href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(
                          m.subject || "your message"
                        )}`}
                        data-testid={`message-reply-${m.id}`}
                        className="brut pcs-bg-pink text-white px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press"
                      >
                        Reply
                      </a>
                      <button
                        type="button"
                        onClick={() => del(m)}
                        disabled={busy}
                        data-testid={`message-delete-${m.id}`}
                        className="brut bg-white px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
